import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LuGauge,
  LuGlobe,
  LuTerminal,
  LuCog,
  LuActivity,
  LuCpu,
  LuDatabase,
  LuArchive,
  LuHardDrive,
  LuBox,
  LuFileText,
  LuMail,
  LuLayers,
  LuListTree,
  LuTimer,
  LuArrowDownToLine,
  LuArrowUpFromLine,
  LuTrash,
} from "react-icons/lu";
import type { ComponentType } from "react";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton, SkeletonCard } from "@/components/ui/skeleton";
import { ResetCountdown } from "@/components/ui/reset-countdown";

type UsageResponse = Awaited<ReturnType<typeof api.platform.usage>>;
type Metric = Extract<UsageResponse, { available: true }>["metrics"][number];

const STATUS_COLOURS: Record<Metric["status"], { bar: string; chip: string; chipText: string }> = {
  ok: {
    bar: "bg-emerald-500",
    chip: "bg-emerald-500/10 border-emerald-500/30",
    chipText: "text-emerald-400",
  },
  warn: {
    bar: "bg-amber-500",
    chip: "bg-amber-500/10 border-amber-500/30",
    chipText: "text-amber-400",
  },
  alert: {
    bar: "bg-orange-500",
    chip: "bg-orange-500/10 border-orange-500/30",
    chipText: "text-orange-400",
  },
  over: {
    bar: "bg-red-500",
    chip: "bg-red-500/10 border-red-500/30",
    chipText: "text-red-400",
  },
};

interface ServiceMeta {
  label: string;
  blurb: string;
  icon: ComponentType<{ className?: string }>;
  /** Tailwind tone used for the section's left-bar + icon tint. */
  tone: "violet" | "cyan" | "emerald" | "amber" | "blue" | "fuchsia";
}

const SERVICE_META: Record<string, ServiceMeta> = {
  workers: {
    label: "Workers",
    blurb: "Request counts and per-request CPU time.",
    icon: LuLayers,
    tone: "violet",
  },
  d1: {
    label: "D1",
    blurb: "Rows read, rows written, total storage.",
    icon: LuDatabase,
    tone: "blue",
  },
  kv: {
    label: "KV",
    blurb: "Reads, writes, deletes, lists, and total storage.",
    icon: LuArchive,
    tone: "amber",
  },
  r2: {
    label: "R2",
    blurb: "Class A / class B operations and stored bytes.",
    icon: LuHardDrive,
    tone: "cyan",
  },
  durable_objects: {
    label: "Durable Objects",
    blurb: "Request count and wall-clock duration.",
    icon: LuBox,
    tone: "fuchsia",
  },
  pages: {
    label: "Pages",
    blurb: "Static-site builds and bandwidth.",
    icon: LuFileText,
    tone: "emerald",
  },
  email: {
    label: "Email",
    blurb: "Cloudflare Email Routing throughput.",
    icon: LuMail,
    tone: "amber",
  },
  queues: {
    label: "Queues",
    blurb: "Producer + consumer message counts.",
    icon: LuListTree,
    tone: "violet",
  },
};

/** Metric-specific icons for the small chip on each card. */
const METRIC_ICON: Record<string, ComponentType<{ className?: string }>> = {
  "workers.requests": LuActivity,
  "workers.cpu_ms": LuCpu,
  "d1.rows_read": LuArrowUpFromLine,
  "d1.rows_written": LuArrowDownToLine,
  "d1.storage_gb": LuHardDrive,
  "kv.reads": LuArrowUpFromLine,
  "kv.writes": LuArrowDownToLine,
  "kv.deletes": LuTrash,
  "kv.lists": LuListTree,
  "kv.storage_gb": LuHardDrive,
  "r2.storage_gb": LuHardDrive,
  "r2.class_a_ops": LuArrowDownToLine,
  "r2.class_b_ops": LuArrowUpFromLine,
  "do.requests": LuActivity,
  "do.duration_ms": LuTimer,
};

const SECTION_TONE: Record<ServiceMeta["tone"], { icon: string; bar: string; bg: string }> = {
  violet: {
    icon: "text-[var(--color-accent-light)]",
    bar: "bg-[var(--color-accent-light)]",
    bg: "bg-[rgba(139,92,246,0.1)] border-[rgba(139,92,246,0.25)]",
  },
  cyan: {
    icon: "text-[#41d1ff]",
    bar: "bg-[#41d1ff]",
    bg: "bg-[rgba(65,209,255,0.08)] border-[rgba(65,209,255,0.25)]",
  },
  emerald: {
    icon: "text-[#6ee7b7]",
    bar: "bg-[#6ee7b7]",
    bg: "bg-[rgba(66,184,131,0.08)] border-[rgba(66,184,131,0.25)]",
  },
  amber: {
    icon: "text-[#fcd34d]",
    bar: "bg-[#fcd34d]",
    bg: "bg-[rgba(250,176,5,0.08)] border-[rgba(250,176,5,0.25)]",
  },
  blue: {
    icon: "text-[#93c5fd]",
    bar: "bg-[#93c5fd]",
    bg: "bg-[rgba(96,165,250,0.08)] border-[rgba(96,165,250,0.25)]",
  },
  fuchsia: {
    icon: "text-[#d8b4fe]",
    bar: "bg-[#d8b4fe]",
    bg: "bg-[rgba(189,52,254,0.08)] border-[rgba(189,52,254,0.25)]",
  },
};

const SERVICE_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(SERVICE_META).map(([k, v]) => [k, v.label]),
);
void SERVICE_LABEL;

function formatNumber(n: number, unit: string): string {
  if (unit === "GB") return `${n.toFixed(2)} GB`;
  if (unit === "MB") return `${n.toFixed(2)} MB`;
  if (unit === "ms") return `${n.toFixed(1)} ms`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

function MetricCard({ m }: { m: Metric }) {
  const colours = STATUS_COLOURS[m.status];
  const barWidth = Math.min(100, m.percentFree);
  const periodLabel = m.period === "day" ? "today" : m.period === "month" ? "this month" : "current";
  // CPU time is a per-request cap, not a daily total. The "current" we
  // report is the worst-case p99 across scripts. Display accordingly.
  const isPerRequestCap = m.id === "workers.cpu_ms";
  const meta = SERVICE_META[m.service];
  const tone = meta ? SECTION_TONE[meta.tone] : SECTION_TONE.violet;
  const MetricIcon = METRIC_ICON[m.id] ?? meta?.icon ?? LuActivity;
  return (
    <Card>
      <CardBody className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className={`w-8 h-8 rounded-md flex items-center justify-center border shrink-0 ${tone.bg}`}
            >
              <MetricIcon className={`w-3.5 h-3.5 ${tone.icon}`} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-[var(--color-text-subtle)]">
                {meta?.label ?? m.service}
              </p>
              <p className="text-sm font-medium text-[var(--color-text)] mt-0.5 truncate">
                {m.label}
              </p>
            </div>
          </div>
          <span
            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border whitespace-nowrap ${colours.chip} ${colours.chipText}`}
          >
            {m.status}
          </span>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between text-sm">
            <span className="font-mono text-[var(--color-text)]">
              {formatNumber(m.current, m.unit)}
            </span>
            <span className="text-xs text-[var(--color-text-subtle)]">
              of {m.free !== null ? formatNumber(m.free, m.unit) : "unlimited"} free
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-[var(--color-bg)] overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${colours.bar}`}
              style={{ width: `${barWidth}%` }}
            />
          </div>
          <div className="flex justify-between items-center text-[11px] text-[var(--color-text-subtle)]">
            {isPerRequestCap ? (
              <span>p99 today vs per-request cap</span>
            ) : (
              <span>{m.percentFree.toFixed(1)}% used {periodLabel}</span>
            )}
            {m.period !== "total" && (
              <ResetCountdown period={m.period} compact />
            )}
          </div>
          {!isPerRequestCap && m.period !== "total" && m.projected > m.current && (
            <p className="text-[11px] text-[var(--color-text-subtle)] -mt-1">
              projecting {formatNumber(m.projected, m.unit)} by{" "}
              {m.period === "day" ? "midnight UTC" : "month end"}
            </p>
          )}
        </div>

        <p className="text-[11px] leading-relaxed text-[var(--color-text-muted)]">{m.description}</p>
      </CardBody>
    </Card>
  );
}

function groupByService(metrics: Metric[]): Record<string, Metric[]> {
  const out: Record<string, Metric[]> = {};
  for (const m of metrics) {
    (out[m.service] ??= []).push(m);
  }
  return out;
}

type Scope = "product" | "console" | "services";

export function PlatformPage() {
  const [scope, setScope] = useState<Scope>("product");
  const usage = useQuery({
    queryKey: ["console", "platform", "usage", scope],
    queryFn: () => api.platform.usage(scope),
    refetchInterval: 60_000,
  });

  return (
    <>
      <PageHeader
        icon={LuGauge}
        title="Platform"
        description="Cloudflare resource usage against the free-tier ceilings. Refreshes every minute."
      />
      <div className="p-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ScopeTabs scope={scope} onChange={setScope} />
          <div className="flex flex-wrap items-center gap-2">
            <ResetCountdown period="day" />
            <ResetCountdown period="month" />
          </div>
        </div>

        {usage.isLoading && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} rows={2} />
              ))}
            </div>
          </div>
        )}

        {usage.data && !usage.data.available && (
          <Card>
            <CardHeader>
              <CardTitle>Platform monitoring not configured</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3 text-sm text-[var(--color-text-muted)]">
              <p>{usage.data.message}</p>
              <pre className="text-xs bg-[var(--color-bg)] border border-[var(--color-border)] rounded-md p-3 overflow-x-auto">
{`cd apps/console-api
wrangler secret put CLOUDFLARE_ACCOUNT_ID
wrangler secret put CLOUDFLARE_API_TOKEN  # Account.Analytics:Read scope`}
              </pre>
              <p className="text-xs text-[var(--color-text-subtle)]">
                The token only needs read access to Account Analytics. No
                write scopes required.
              </p>
            </CardBody>
          </Card>
        )}

        {usage.data && usage.data.available && (
          <>
            {/* Cost / plan / deploys summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
                    Projected cost (this period)
                  </p>
                  <p className="text-2xl font-semibold text-[var(--color-text)] mt-1 tabular-nums">
                    ${usage.data.projectedCostUsd.toFixed(2)}
                  </p>
                  <p className="text-[11px] text-[var(--color-text-subtle)] mt-1">
                    Linear extrapolation of paid-tier overage charges.
                  </p>
                </div>
              </Card>
              <Card>
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
                    Workers plan
                  </p>
                  <p className="text-2xl font-semibold text-[var(--color-text)] mt-1 capitalize">
                    {usage.data.plan?.usageModel ?? "unknown"}
                  </p>
                  <p className="text-[11px] text-[var(--color-text-subtle)] mt-1">
                    {usage.data.plan?.greenCompute === true
                      ? "Green compute · "
                      : ""}
                    {usage.data.plan?.usageModel === "standard"
                      ? "Standard plan, 30s CPU cap"
                      : usage.data.plan?.usageModel === "bundled"
                        ? "Free tier, 10ms CPU cap"
                        : "—"}
                  </p>
                </div>
              </Card>
              <Card>
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
                    Workers on this account
                  </p>
                  <p className="text-2xl font-semibold text-[var(--color-text)] mt-1 tabular-nums">
                    {usage.data.deploys.length}
                  </p>
                  <p className="text-[11px] text-[var(--color-text-subtle)] mt-1">
                    {usage.data.deploys[0]
                      ? `last deploy ${new Date(usage.data.deploys[0].lastModified).toLocaleString()}`
                      : "no recent deploys"}
                  </p>
                </div>
              </Card>
            </div>

            {/* Recent deploys */}
            {usage.data.deploys.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Recent deploys</CardTitle>
                </CardHeader>
                <CardBody className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--color-bg)]/40">
                      <tr className="text-left text-[var(--color-text-subtle)] border-b border-[var(--color-border)]">
                        <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium">
                          Worker
                        </th>
                        <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium text-right">
                          Last deploy
                        </th>
                        <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium text-right">
                          Created
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {usage.data.deploys.map((d) => (
                        <tr
                          key={d.name}
                          className="border-b border-[var(--color-border)] last:border-0"
                        >
                          <td className="px-5 py-2 font-mono text-[var(--color-text)]">
                            {d.name}
                          </td>
                          <td className="px-5 py-2 text-right text-xs text-[var(--color-text-muted)] font-mono tabular-nums">
                            {new Date(d.lastModified).toLocaleString()}
                          </td>
                          <td className="px-5 py-2 text-right text-xs text-[var(--color-text-subtle)] font-mono tabular-nums">
                            {new Date(d.createdOn).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardBody>
              </Card>
            )}

            {usage.data.errors.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Partial data</CardTitle>
                </CardHeader>
                <CardBody className="text-xs space-y-2 text-[var(--color-text-muted)]">
                  {usage.data.errors.map((e) => (
                    <div key={e.section}>
                      <span className="font-mono text-[var(--color-text-subtle)]">
                        {e.section}:
                      </span>{" "}
                      {e.message}
                    </div>
                  ))}
                </CardBody>
              </Card>
            )}

            {Object.entries(groupByService(usage.data.metrics)).map(([service, metrics]) => {
              const meta = SERVICE_META[service];
              const tone = meta ? SECTION_TONE[meta.tone] : SECTION_TONE.violet;
              const Icon = meta?.icon ?? LuLayers;
              const overCount = metrics.filter((mm) => mm.status === "over").length;
              const alertCount = metrics.filter((mm) => mm.status === "alert").length;
              return (
                <section key={service} className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`block w-1 h-7 rounded-full ${tone.bar}`} />
                      <div
                        className={`w-9 h-9 rounded-md flex items-center justify-center border ${tone.bg}`}
                      >
                        <Icon className={`w-4 h-4 ${tone.icon}`} />
                      </div>
                      <div className="min-w-0">
                        <h2 className="text-sm font-semibold text-[var(--color-text)] flex items-center gap-2">
                          {meta?.label ?? service}
                          <span className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)] font-normal">
                            {metrics.length} metric{metrics.length === 1 ? "" : "s"}
                          </span>
                        </h2>
                        {meta?.blurb && (
                          <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5 truncate">
                            {meta.blurb}
                          </p>
                        )}
                      </div>
                    </div>
                    {(overCount > 0 || alertCount > 0) && (
                      <div className="flex items-center gap-2 shrink-0">
                        {overCount > 0 && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border bg-[rgba(237,66,69,0.1)] border-[rgba(237,66,69,0.3)] text-[#fca5a5]">
                            {overCount} over
                          </span>
                        )}
                        {alertCount > 0 && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border bg-[rgba(249,115,22,0.1)] border-[rgba(249,115,22,0.3)] text-[#fdba74]">
                            {alertCount} alert
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {metrics.map((m) => (
                      <MetricCard key={m.id} m={m} />
                    ))}
                  </div>
                </section>
              );
            })}
          </>
        )}
      </div>
    </>
  );
}

function ScopeTabs({
  scope,
  onChange,
}: {
  scope: Scope;
  onChange: (s: Scope) => void;
}) {
  const tabs: {
    id: Scope;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    description: string;
  }[] = [
    {
      id: "product",
      label: "Product",
      icon: LuGlobe,
      description: "destroy.network public worker, product D1, KV, R2, Durable Objects.",
    },
    {
      id: "console",
      label: "Console",
      icon: LuTerminal,
      description:
        "Operator console worker + console DB. Quotas isolated from product.",
    },
    {
      id: "services",
      label: "Services",
      icon: LuCog,
      description:
        "Cron workers + side-services: monthly-digest, domain-health-checker, email-forwarder, status-monitor.",
    },
  ];
  const active = tabs.find((t) => t.id === scope);
  return (
    <div>
      <div className="inline-flex rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-1">
        {tabs.map((t) => {
          const isActive = t.id === scope;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onChange(t.id)}
              className={[
                "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors",
                isActive
                  ? "bg-[rgba(139,92,246,0.12)] text-[var(--color-text)] border border-[rgba(139,92,246,0.3)]"
                  : "text-[var(--color-text-muted)] hover:text-[var(--color-text)] border border-transparent",
              ].join(" ")}
            >
              <t.icon
                className={`w-3.5 h-3.5 ${
                  isActive
                    ? "text-[var(--color-accent-light)]"
                    : "text-[var(--color-text-subtle)]"
                }`}
              />
              {t.label}
            </button>
          );
        })}
      </div>
      {active && (
        <p className="text-xs text-[var(--color-text-subtle)] mt-2">{active.description}</p>
      )}
    </div>
  );
}
